import { useEffect, useState } from "react";
import * as XLSX from 'xlsx';
import '../css/SalesReport.css';

export default function SalesReport() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const rowsPerPage = 10;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/salesreport.php`);
        const json = await res.json();
        if (json.status && Array.isArray(json.data)) {
          setData(json.data);
        } else {
          setError(json.message || "Failed to fetch data");
        }
      } catch (err) {
        setError("Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Filter by start and end date
  let filteredRows = data;
  if (startDate) {
    filteredRows = filteredRows.filter(row => row.date.slice(0, 10) >= startDate);
  }
  if (endDate) {
    filteredRows = filteredRows.filter(row => row.date.slice(0, 10) <= endDate);
  }

  // Group by date (YYYY-MM-DD)
  const groupedByDate = {};
  filteredRows.forEach(row => {
    const dateKey = row.date.slice(0, 10);
    if (!groupedByDate[dateKey]) {
      groupedByDate[dateKey] = { expenses: 0, sale: 0, purchase: 0 };
    }
    groupedByDate[dateKey].expenses += parseFloat(row.expenses) || 0;
    groupedByDate[dateKey].sale += parseFloat(row.sale) || 0;
    groupedByDate[dateKey].purchase += parseFloat(row.purchase) || 0;
  });
  let groupedRows = Object.entries(groupedByDate).map(([date, { expenses, sale, purchase }]) => ({
    date,
    expenses,
    sale,
    purchase,
    income: sale - (expenses + purchase),
  })).sort((a, b) => b.date.localeCompare(a.date));

  // Pagination
  const totalRows = groupedRows.length;
  const totalPages = Math.ceil(totalRows / rowsPerPage);
  const paginatedRows = groupedRows.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  // Totals
  const totalExpenses = groupedRows.reduce((sum, row) => sum + row.expenses, 0);
  const totalSale = groupedRows.reduce((sum, row) => sum + row.sale, 0);
  const totalPurchase = groupedRows.reduce((sum, row) => sum + row.purchase, 0);
  const totalIncome = groupedRows.reduce((sum, row) => sum + row.income, 0);

  // Export to Excel
  const handleExportAllToExcel = () => {
    setExporting(true);
    try {
      const exportData = groupedRows.map((row) => ({
        'Date': row.date,
        'Expenses': row.expenses,
        'Purchase': row.purchase,
        'Sale': row.sale,
        'Income': row.income,
      }));
      // Add summary row
      exportData.push({
        'Date': 'TOTAL',
        'Expenses': totalExpenses,
        'Purchase': totalPurchase,
        'Sale': totalSale,
        'Income': totalIncome,
      });
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);
      ws['!cols'] = [
        { wch: 15 }, // Date
        { wch: 15 }, // Expenses
        { wch: 15 }, // Purchase
        { wch: 15 }, // Sale
        { wch: 15 }, // Income
      ];
      XLSX.utils.book_append_sheet(wb, ws, 'Sales Report');
      const fileName = `sales_report_${new Date().toISOString().slice(0, 10)}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (error) {
      alert('Failed to export to Excel. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="salesreport-container">
      <h1 className="salesreport-title">Sales Report</h1>
      <div className="salesreport-subtitle">View and export your sales, expenses, and income summary</div>
      <div className="dashboard-card" style={{marginBottom: 24}}>
        <h3>Filter Reports</h3>
        <form className="salesreport-filter-form">
          <div>
            <label className="block text-sm font-medium mb-1">Start Date</label>
            <input
              type="date"
              id="start-date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              placeholder="Select start date"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">End Date</label>
            <input
              type="date"
              id="end-date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              placeholder="Select end date"
            />
          </div>
        </form>
      </div>
      <div className="dashboard-card">
        <h3>Summary Table</h3>
        <div className="salesreport-actions">
          <button
            onClick={handleExportAllToExcel}
            disabled={exporting}
            className="salesreport-btn"
            title="Export to Excel"
          >
            {exporting ? 'Exporting...' : 'Export Excel'}
          </button>
        </div>
        <div className="table-container">
          {loading ? (
            <div className="text-center py-8 text-gray-400">Loading...</div>
          ) : error ? (
            <div className="text-center py-8 text-red">{error}</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Expenses</th>
                  <th>Purchase</th>
                  <th>Sale</th>
                  <th>Income</th>
                </tr>
              </thead>
              <tbody>
                {paginatedRows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-8 text-gray-400">No data found.</td>
                  </tr>
                ) : (
                  paginatedRows.map((row, idx) => (
                    <tr key={row.date + idx}>
                      <td>{row.date}</td>
                      <td className="text-red">₹ {row.expenses.toFixed(2)}</td>
                      <td className="text-brand">₹ {row.purchase.toFixed(2)}</td>
                      <td className="text-brand">₹ {row.sale.toFixed(2)}</td>
                      <td className="text-green">₹ {row.income.toFixed(2)}</td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td style={{textAlign: 'right'}}>Total</td>
                  <td className="text-red">₹ {totalExpenses.toFixed(2)}</td>
                  <td className="text-brand">₹ {totalPurchase.toFixed(2)}</td>
                  <td className="text-brand">₹ {totalSale.toFixed(2)}</td>
                  <td className="text-green">₹ {totalIncome.toFixed(2)}</td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
        {/* Pagination Controls */}
        {totalPages > 1 && !loading && !error && (
          <div className="flex justify-center items-center gap-2 mt-6 overflow-x-auto">
            <button
              className="salesreport-btn"
              style={{background: '#e5e7eb', color: '#22223b'}}
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Prev
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                className="salesreport-btn"
                style={currentPage === page ? {background: '#2563eb', color: '#fff'} : {background: '#e5e7eb', color: '#22223b'}}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </button>
            ))}
            <button
              className="salesreport-btn"
              style={{background: '#e5e7eb', color: '#22223b'}}
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
} 